/**
 * **Site & flow** (B0.5, docs/11-interface.md): per-site layout analytics —
 * the cycle-time curve, the calibration readout, the per-machine load — and
 * the rule that every number is *pointable*, so clicking the spike flies the
 * camera to the queue that made it. Since M1.1 these are no longer a window
 * of their own: [`Calibration`] and [`Machines`] are the line panel's folds,
 * and [`ViewStrip`] (which field the floor glows with) lives in the dock.
 *
 * Its job is stated in the roadmap's done-when and is worth keeping in view
 * while reading this file: **it has to explain a bottleneck to someone who did
 * not build the layout.** So the order is the order a diagnosis goes in. What is
 * the fab doing right now, and is that what the curve said it would do. Where on
 * the curve are we sitting — inside the knee, at it, or past it. Which machine
 * is the reason. And then: go and look at that machine.
 *
 * Everything here is DOM, at tick rate, from the store — React never touches the
 * per-frame path (docs/13-architecture.md). The one thing it reaches for outside
 * the store is the camera, through a `focus` callback, because flying to a cell
 * is presentation and has no business as sim state.
 *
 * **On the chart.** Two facets, one x-axis (the release dial) — never two
 * y-scales on one plot, which would make cycle time and throughput look like
 * they cross somewhere meaningful. Each facet is a single series, so it needs no
 * legend and is labelled by its own title. Measured points are solid; the
 * saturated stretch past capacity is dashed with hollow markers, because there
 * the cycle-time number is a lower bound rather than a prediction and a reader
 * must be able to tell the two apart without being told.
 */

import { useSyncExternalStore } from 'react';
import type { CalibrationUi, CurvePointUi, FloorHost, FloorView, ToolLoadUi } from '../bridge';
import { snapshot, subscribe, type HudState } from './store';

/**
 * Where the camera is asked to go — a plot cell, or `null` for the whole plot.
 * Supplied by the shell, because the camera is presentation and has no business
 * being sim state, and React has no business holding a renderer.
 */
export type FlyTo = (cell: { x: number; y: number } | null) => void;

/**
 * The chart's two hues, matching the floor plane's own tints so the overlay and
 * the floor speak one colour language: amber is the queue's colour on the floor
 * and the cycle-time facet's colour here, cyan is utilization in both places.
 */
const CYCLE_HUE = '#f0a860';
const THROUGHPUT_HUE = '#59c8d8';

/** The three fields the published plane can carry, with what each answers. */
const VIEWS: { id: FloorView; label: string; hint: string }[] = [
  { id: 'wip', label: 'WIP', hint: 'where the work is standing' },
  { id: 'utilization', label: 'Load', hint: 'how hard each machine is working' },
  { id: 'waiting', label: 'Nobody', hint: 'where the floor is waiting for a person' },
];

/** Look at a thing on the floor: select it in the sim, fly the camera to it. */
function looker(host: FloorHost, flyTo: FlyTo) {
  return (x: number, y: number) => {
    host.send({ kind: 'select', x, y });
    flyTo({ x, y });
  };
}

/**
 * The calibration fold: what the benchmark is doing, the seam's own numbers
 * (only what the readings above do not already show), and the measured curve.
 */
export function Calibration({ host, flyTo }: { host: FloorHost; flyTo: FlyTo }) {
  const hud = useSyncExternalStore(subscribe, snapshot);
  const ui = hud.ui;
  if (!ui) return null;
  const { calibration, flow } = ui;
  const look = looker(host, flyTo);
  const deepest = flow.tools.reduce<ToolLoadUi | null>(
    (most, tool) => (!most || tool.queueLots > most.queueLots ? tool : most),
    null,
  );
  return (
    <div className="siteflow">
      <header>
        <Status calibration={calibration} />
        <span className="spacer" />
        <button
          className="collapse"
          onClick={() => host.send({ kind: 'recalibrate' })}
          title="Throw the curve away and measure again — for everything the signature cannot see"
        >
          Recalibrate
        </button>
      </header>
      <Seam state={hud} />
      <Curve
        calibration={calibration}
        onPoint={() => {
          // "Click the cycle-time spike; the camera flies to the queue"
          // (docs/11-interface.md). The spike *is* the deepest pile on the
          // floor, so that is where it goes.
          if (deepest) look(deepest.x, deepest.y);
        }}
      />
    </div>
  );
}

/** The machines fold: every tool's load this hour, its queue and its state, click to fly. */
export function Machines({ host, flyTo }: { host: FloorHost; flyTo: FlyTo }) {
  const hud = useSyncExternalStore(subscribe, snapshot);
  const ui = hud.ui;
  if (!ui) return null;
  return (
    <div className="siteflow">
      <Load tools={ui.flow.tools} onLook={looker(host, flyTo)} />
    </div>
  );
}

/** The floor plane's field and the whole-plot camera — the dock's view strip. */
export function ViewStrip({ host, flyTo }: { host: FloorHost; flyTo: FlyTo }) {
  const hud = useSyncExternalStore(subscribe, snapshot);
  const view = hud.ui?.floorView ?? 'wip';
  return (
    <div className="dock-view">
      {VIEWS.map((option) => (
        <button
          key={option.id}
          title={`floor glow: ${option.hint}`}
          className={view.startsWith(option.id) ? 'active' : ''}
          onClick={() => host.send({ kind: 'setFloorView', view: option.id })}
        >
          {option.label}
        </button>
      ))}
      <button onClick={() => flyTo(null)} title="the whole plot">
        ⌂
      </button>
    </div>
  );
}

/** What the benchmark is doing, in three words and a bar. */
function Status({ calibration }: { calibration: CalibrationUi }) {
  const measuring = calibration.status === 'measuring';
  return (
    <div className="status" title={`floor signature ${calibration.signature}`}>
      <span className={measuring ? 'chip measuring' : 'chip'}>
        {measuring
          ? `measuring at ${calibration.measuringAt}/day`
          : `settling · ${calibration.phaseRemainingHours.toFixed(0)} h left`}
      </span>
      <span className="bar">
        <span style={{ width: `${calibration.phaseMille / 10}%` }} />
      </span>
    </div>
  );
}

/**
 * **The seam, on screen.** The left column is what the floor is actually doing;
 * the right is what fast-forward would report for this site *without simulating
 * any of it*, taken from the measured curve alone.
 *
 * Watching those two columns agree while the fab runs is the same comparison the
 * harness asserts on (`floor_b05`), which is the point of putting it here rather
 * than only in a test: a seam you can watch is a seam you notice leaking.
 */
function Seam({ state }: { state: HudState }) {
  const ui = state.ui;
  if (!ui) return null;
  const { calibration } = ui;
  if (!calibration.ready) {
    return (
      <section className="seam">
        <p className="muted">
          Nothing measured yet — {calibration.phaseRemainingHours.toFixed(0)} theater hours of{' '}
          {calibration.status} to go, and then a day of counting. A fab has to
          fill before what comes out of it is answering the question you asked.
          Change the layout, the tool set or the shift and it starts again.
        </p>
      </section>
    );
  }
  return (
    <section className="seam">
      <div className="seam-head">
        <span />
        <span>watched</span>
        <span>fast-forward</span>
      </div>
      <SeamRow
        label="D₀ modifier"
        watched={`${(calibration.liveD0Mille / 1000).toFixed(2)}×`}
        forecast={calibration.forecast ? `${(calibration.forecastD0Mille / 1000).toFixed(2)}×` : '—'}
      />
      <div className="row">
        <span className="label">watching</span>
        <span className="value">
          {calibration.watching
            ? `${calibration.watchedLots} lots out this window · ${(calibration.phaseMille / 10).toFixed(0)}% through it`
            : `settling after a change · ${calibration.phaseRemainingHours.toFixed(0)} theater hours to go`}
        </span>
      </div>
      <div className="row">
        <span className="label">capacity found</span>
        <span className="value">
          {calibration.capacityWafersPerWeek.toLocaleString()} wafers/wk · {calibration.windows}{' '}
          windows over {calibration.measuredHours.toFixed(0)} h
        </span>
      </div>
      <div className="row">
        <span className="label">cleanliness</span>
        <span className="value">
          {(calibration.cleanlinessMille / 1000).toFixed(2)}× the reference bay
        </span>
      </div>
      <div className="row">
        <span
          className="label"
          title="how tightly the fleet holds its process — models × condition, visits-weighted over the route. What the parametric quality of the line's output rests on; it rides on the curve like cleanliness does."
        >
          capability
        </span>
        <span className="value">
          {(calibration.capabilityMille / 1000).toFixed(2)}× a reference line
          {ui.flow.revealedMille > 0
            ? ` · final test reads ${(ui.flow.revealedMille / 1000).toFixed(2)}× on recent lots`
            : ' · nothing through final test yet'}
        </span>
      </div>
    </section>
  );
}

function SeamRow({
  label,
  watched,
  forecast,
}: {
  label: string;
  watched: string;
  forecast: string;
}) {
  return (
    <div className="seam-row">
      <span className="label">{label}</span>
      <span className="value">{watched}</span>
      <span className="value forecast">{forecast}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The curve
 * ------------------------------------------------------------------ */

const CHART_W = 520;
const FACET_H = 78;
const PAD = { left: 40, right: 12, top: 12, bottom: 4 };

/**
 * The measured curves: cycle time and throughput against the release dial, as
 * two facets over one shared x-axis.
 *
 * A reader is meant to get one thing out of this and it is the *knee* — the
 * loading past which cycle time turns upward while throughput has stopped
 * rising. That is the whole industrial-engineering lesson of the production doc,
 * and it is why the two facets are stacked and aligned rather than overlaid: the
 * eye should be comparing the two shapes at the same x, not decoding two scales.
 */
function Curve({
  calibration,
  onPoint,
}: {
  calibration: CalibrationUi;
  onPoint: () => void;
}) {
  const points = calibration.points;
  if (points.length < 2) {
    return (
      <section className="curve">
        <p className="muted">
          {points.length === 0
            ? 'The cycle-time curve needs a measured point to start from.'
            : 'One point measured. Move the release dial and the curve fills in — every loading you run the fab at becomes a point on it.'}
        </p>
      </section>
    );
  }
  const xs = points.map((p) => p.startsPerDay);
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  const px = (starts: number) =>
    PAD.left + ((starts - x0) / Math.max(x1 - x0, 1)) * (CHART_W - PAD.left - PAD.right);

  return (
    <section className="curve">
      <Facet
        title="cycle time"
        unit="h"
        hue={CYCLE_HUE}
        points={points}
        value={(p) => p.cycleHours}
        format={(v) => v.toFixed(0)}
        px={px}
        onPoint={onPoint}
      />
      <Facet
        title="out"
        unit="lots/day"
        hue={THROUGHPUT_HUE}
        points={points}
        value={(p) => p.lotsOutPerDay}
        format={(v) => v.toFixed(1)}
        px={px}
        onPoint={onPoint}
        axis={{ x0, x1, px }}
      />
      <p className="key">
        <span className="dot solid" style={{ background: CYCLE_HUE }} /> measured ·{' '}
        <span className="dot hollow" style={{ borderColor: CYCLE_HUE }} /> past capacity, where the
        cycle time is a lower bound and the queue is still growing · click to fly to the queue
      </p>
    </section>
  );
}

function Facet({
  title,
  unit,
  hue,
  points,
  value,
  format,
  px,
  onPoint,
  axis,
}: {
  title: string;
  unit: string;
  hue: string;
  points: CurvePointUi[];
  value: (p: CurvePointUi) => number;
  format: (v: number) => string;
  px: (starts: number) => number;
  onPoint: () => void;
  axis?: { x0: number; x1: number; px: (starts: number) => number };
}) {
  const top = Math.max(...points.map(value), 0.001) * 1.15;
  const py = (v: number) => PAD.top + (1 - v / top) * (FACET_H - PAD.top - PAD.bottom);
  const path = (subset: CurvePointUi[]) =>
    subset.map((p, i) => `${i === 0 ? 'M' : 'L'}${px(p.startsPerDay)},${py(value(p))}`).join(' ');

  // The two stretches, split at the first loading the fab could not keep up
  // with. The dashed one is a weaker claim and has to look like one.
  const firstSaturated = points.findIndex((p) => p.saturated);
  const steady = firstSaturated < 0 ? points : points.slice(0, firstSaturated + 1);
  const over = firstSaturated < 0 ? [] : points.slice(Math.max(firstSaturated - 1, 0));
  const current = points.find((p) => p.current);

  return (
    <svg
      className="facet"
      viewBox={`0 0 ${CHART_W} ${FACET_H + (axis ? 16 : 0)}`}
      role="img"
      aria-label={`${title} against release rate`}
    >
      {[0, 0.5, 1].map((at) => (
        <line
          key={at}
          x1={PAD.left}
          x2={CHART_W - PAD.right}
          y1={py(top * at)}
          y2={py(top * at)}
          className="grid"
        />
      ))}
      <text x={4} y={PAD.top + 4} className="axis">
        {format(top)}
      </text>
      <text x={4} y={FACET_H - PAD.bottom} className="axis">
        0
      </text>
      <text x={4} y={PAD.top - 4} className="facet-title">
        {title} <tspan className="axis">{unit}</tspan>
      </text>

      <path d={path(steady)} fill="none" stroke={hue} strokeWidth={2} strokeLinejoin="round" />
      {over.length > 1 ? (
        <path
          d={path(over)}
          fill="none"
          stroke={hue}
          strokeWidth={2}
          strokeDasharray="4 3"
          strokeLinejoin="round"
        />
      ) : null}

      {points.map((p) => (
        <g key={p.startsPerDay} onClick={onPoint} className="mark">
          <circle
            cx={px(p.startsPerDay)}
            cy={py(value(p))}
            r={4}
            fill={p.saturated ? '#141a20' : hue}
            stroke={hue}
            strokeWidth={2}
          />
          {p.current ? (
            <circle
              cx={px(p.startsPerDay)}
              cy={py(value(p))}
              r={7.5}
              fill="none"
              stroke={hue}
              strokeWidth={1}
              opacity={0.7}
            />
          ) : null}
          {/* Hit target bigger than the mark, and a native tooltip on it. */}
          <circle cx={px(p.startsPerDay)} cy={py(value(p))} r={12} fill="transparent">
            <title>
              {`${p.startsPerDay} starts/day → ${format(value(p))} ${unit}` +
                ` · ${p.windows} windows, ${p.completions} lots` +
                (p.saturated ? ' · past capacity: a lower bound' : '')}
            </title>
          </circle>
        </g>
      ))}

      {/* One direct label, on the loading in force. Never a number on every
          point — that is the fastest way to make a small chart unreadable. */}
      {current ? (
        <text
          x={px(current.startsPerDay)}
          y={py(value(current)) - 12}
          className="callout"
          textAnchor="middle"
        >
          {format(value(current))}
        </text>
      ) : null}

      {axis
        ? points.map((p) => (
            <text
              key={p.startsPerDay}
              x={axis.px(p.startsPerDay)}
              y={FACET_H + 12}
              className="axis"
              textAnchor="middle"
            >
              {p.startsPerDay}
            </text>
          ))
        : null}
      {axis ? (
        <text x={CHART_W - PAD.right} y={FACET_H + 12} className="axis" textAnchor="end">
          starts/day
        </text>
      ) : null}
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * The utilization heatmap, as rows
 * ------------------------------------------------------------------ */

/**
 * Every machine, busiest first, with the sentence the sim wrote about what it is
 * doing. This is the list a diagnosis is actually read off, and the reason it
 * carries *words* as well as a bar is B0.4's lesson: a tool that is full, a tool
 * that is broken and a tool nobody has come to are three different problems with
 * three different fixes, and a bar alone would make all three look like "busy".
 */
function Load({ tools, onLook }: { tools: ToolLoadUi[]; onLook: (x: number, y: number) => void }) {
  if (tools.length === 0) return null;
  const rows = [...tools].sort((a, b) => b.recentMille - a.recentMille);
  return (
    <section className="load">
      <div className="load-head">
        <span>machine</span>
        <span>this hour</span>
        <span>queue</span>
      </div>
      {rows.map((tool) => (
        <button key={tool.id} className="load-row" onClick={() => onLook(tool.x, tool.y)}>
          <span className="name">{tool.name}</span>
          <span className="meter">
            <span
              style={{
                width: `${Math.min(tool.recentMille / 10, 100)}%`,
                background: tool.starved ? '#e0574f' : THROUGHPUT_HUE,
              }}
            />
          </span>
          <span className="pct">{(tool.recentMille / 10).toFixed(0)}%</span>
          <span className="queue">{tool.queueLots > 0 ? `${tool.queueLots} lots` : '—'}</span>
          <span className={tool.starved ? 'state over' : 'state'}>{tool.state}</span>
        </button>
      ))}
    </section>
  );
}
